import AuthLayout from "./AuthLayout";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { skip2FactorAuthentication } from "../../store/authSlice";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import EmailIcon from "../../assets/auth/email-icon.svg";
import MobileIcon from "../../assets/auth/mobile-icon.svg";
import FactorAuthIllustration from "../../assets/auth/2-factor-auth.svg";
import { loginAfterRegistration } from "../../services/auth/invitation_subscription";
import { useAppState } from "../../hooks/useAppState";
import { toggleNotificationSetting } from "../../store/profileSlice";

export function FactorAuth(): JSX.Element {
  // Get location to access navigation state
  const location = useLocation();
  const { email, password, registrationType, invitation, organization } =
    location.state || {};

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppState();
  const [type, setType] = useState("");
  const [error, setError] = useState("");
  const [notificationSettings, setNotificationSettings] = useState({
    email: false,
    SMS: false,
  });

  useEffect(() => {
    // Log in user using the credentials passed from registration
    const performLogin = async () => {
      if (email && password && registrationType) {
        try {
          const loginResult = await loginAfterRegistration(email, password);
          if (!loginResult.success) {
            setError("Failed to log in after registration. Please try again.");
          }
        } catch (error) {
          console.error("Login error:", error);
          setError("Failed to log in after registration.");
        }
      }
    };

    performLogin();
  }, [email, password]);

  const handleNotificationToggle = async (type: any) => {
    setType(type);
    try {
      await dispatch(toggleNotificationSetting(type)).unwrap();
      if (type === "Email") {
        setNotificationSettings({
          ...notificationSettings,
          email: !notificationSettings.email,
          SMS: false,
        });
      } else {
        setNotificationSettings({
          ...notificationSettings,
          SMS: !notificationSettings.SMS,
          email: false,
        });
      }
    } catch (error) {
      console.error("Failed to update notification setting:", error);
      alert("Failed to update notification setting. Please try again.");
    }
  };

  const handleSkip = async () => {
    if (user) {
      await dispatch(skip2FactorAuthentication(user)).unwrap();

      if (registrationType) {
        if (registrationType === "invitation") {
          if (invitation.planType === "expert") {
            navigate(`/expert/dashboard?org=${invitation.orgId}`);
          } else {
            navigate(`/user/dashboard?org=${invitation.orgId}`);
          }
        } else if (registrationType === "owner") {
          if (organization.planType === "expert") {
            navigate(`/expert/dashboard?org=${organization.id}`);
          } else {
            navigate(`/user/dashboard?org=${organization.id}`);
          }
        }
      } else {
        if (user?.role === "Customer") {
          navigate("/user/dashboard");
        } else if (user?.role === "Expert" || user?.role === "expert_owner") {
          navigate("/expert/dashboard");
        } else if (user?.role === "supporty_admin") {
          navigate("/admin/dashboard");
        }
      }
    }
  };

  const handleSendOtp = async (email: string, type: string) => {
    if (!email) {
      return;
    }

    try {
      const response = await fetch(
        "https://us-central1-supporty-staging.cloudfunctions.net/sendOTP",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phNumber: "", email, type }),
        }
      );

      const result = await response.json();
      if (result.success) {
        navigate("/signup/otp-verify", {
          state: {
            phoneNumber: "",
            email,
            type: "Email",
            registrationType: registrationType,
            invitation: invitation,
            organization: organization,
          },
        });
      } else {
        setError("Failed to send verification code.");
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleNext = () => {
    if (type === "Email") {
      handleSendOtp(user?.email, "email");
    } else {
      navigate("/signup/mobile-otp-verify");
      setNotificationSettings({
        ...notificationSettings,
        SMS: true,
      });
    }
  };

  return (
    <AuthLayout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-row w-full shadow-lg rounded-lg overflow-hidden h-[100vh]">
          {/* Left Column: Signup Form */}
          <div className="flex-1 flex flex-col justify-center p-8 bg-white space-y-6 items-center">
            <div className="">
              <div className="text-center">
                <div className="flex justify-center items-center gap-5">
                  <button></button>
                  <h2 className="text-title nunito-extrabold text-[48px] text-[#0A0A0A]">
                    2-Factor Authentication
                  </h2>
                </div>

                <p className="mt-2 text-[24px] nunito-regular  text-[#696969] text-center max-w-[43rem] mx-auto">
                  Each time you log in, a 2FA code will be sent to your selected
                  method for added security
                </p>
              </div>
              <div className="flex justify-center gap-2 mt-12">
                <div className=" w-[600px] ">
                  <div className="border  border-[#C4C4C4] rounded-[16px] flex flex-row p-6  items-center justify-between gap-3 ">
                    <div>
                      <img
                        src={EmailIcon}
                        alt="Help Illustration"
                        className="mx-auto text-[#919191]"
                      />
                    </div>
                    <div>
                      <p className="text-[#919191] text-[24px] nunito-semibold">
                        Enable using email
                      </p>
                      <p
                        className="text-[#919191] text-[20px] nunito-semibold "
                        style={{
                          marginTop: "8px",
                        }}
                      >
                        Will send you 2FA code on your email
                      </p>
                    </div>
                    <div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          onClick={() => handleNotificationToggle("Email")}
                          checked={notificationSettings.email}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-[#A8A8A8] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#18ACF1]/30 rounded-full peer peer-checked:bg-[#18ACF1] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                      </label>
                    </div>
                  </div>
                  <div className="border border-[#C4C4C4] rounded-[16px] flex flex-row p-6  items-center justify-between mt-12">
                    <div>
                      <img
                        src={MobileIcon}
                        alt="Help Illustration"
                        className="mx-auto"
                      />
                    </div>
                    <div>
                      <p className="text-[#919191] nunito-semibold text-[24px]">
                        Enable using mobile number
                      </p>
                      <p
                        className="text-[#919191] nunito-semibold text-[20px] "
                        style={{
                          marginTop: "8px",
                        }}
                      >
                        Will send you 2FA code vai text SMS
                      </p>
                    </div>
                    <div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings.SMS}
                          onClick={() => handleNotificationToggle("SMS")}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-[#A8A8A8] peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#18ACF1]/30 rounded-full peer peer-checked:bg-[#18ACF1] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                      </label>
                    </div>
                  </div>
                  {error && (
                    <div className="text-[#EF4444] text-center text-lg mt-4">
                      {error}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-6 mt-12">
              <button
                type="submit"
                onClick={handleNext}
                disabled={
                  notificationSettings.email === false &&
                  notificationSettings.SMS === false
                }
                className={`mt-2 w-[320px]  flex items-center justify-center px-4 py-2 text-white font-medium rounded-md ${"bg-primary hover:bg-primary"} focus:ring-2 focus:ring-[#F15D18] disabled:bg-gray-400 disabled:cursor-not-allowed`}
              >
                {"Next"}
              </button>
              <button
                type="button"
                onClick={handleSkip}
                disabled={
                  notificationSettings.email || notificationSettings.SMS
                }
                className={`mt-2 w-[320px]  flex items-center justify-center px-4 py-2 font-medium rounded-md  border border-[#F15D18]
                 focus:ring-2 focus:ring-orange-500 text-[#F15D18]  disabled:cursor-not-allowed`}
              >
                Skip to Dashboard
              </button>
            </div>
          </div>

          {/* Right Column: Help Section */}
          <div className="w-1/3 flex flex-col justify-center bg-[#FBFAF9] p-12">
            <img
              src={FactorAuthIllustration}
              alt="Help Illustration"
              className="mx-auto mb-16"
            />

            <p className="text-start w-[380px]   text-[#919191] text-[20px] text-text nunito-regular">
              2FA enhances security by requiring a second verification step to
              confirm your identity and access your account
            </p>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
